import { useState } from "react";
import { Star, Eye, Flame, Truck, ShieldCheck, BadgeCheck, Check, Award, Leaf, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, GALLERY, PACKS, TRUST_BADGES } from "@/lib/content";
import { formatINR } from "@/lib/useReveal";
import { useCart } from "@/context/CartContext";
import hpFire from "@/assets/hp-fire.jpeg";

const ICONS = { Truck, ShieldCheck, BadgeCheck };

const CERTIFICATIONS = [
  { label: "GMP Certified", icon: Award },
  { label: "ISO 9001:2015", icon: ShieldCheck },
  { label: "FSSAI", icon: FlaskConical },
  { label: "AYUSH", icon: Leaf },
  { label: "100% Natural", icon: BadgeCheck },
];

export const ProductHero = () => {
  const [activeImg, setActiveImg] = useState(0);
  const [selectedPack, setSelectedPack] = useState(PACKS[1].id);
  const { addPack, setCheckoutOpen, setDrawerOpen } = useCart();

  const pack = PACKS.find((p) => p.id === selectedPack);

  const handleAddToCart = () => addPack(pack, { openDrawer: true });
  const handleBuyNow = () => {
    addPack(pack, { openDrawer: false });
    setDrawerOpen(false);
    setCheckoutOpen(true);
  };

  return (
    <section className="relative overflow-hidden">
      {/* Highlighted product background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <img
          src={hpFire}
          alt=""
          className="h-full w-full scale-110 object-cover opacity-[0.12] blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/85 to-[var(--hp-page)]" />
        <div className="absolute -left-24 top-8 h-72 w-72 rounded-full bg-[var(--hp-crimson)]/15 blur-3xl" />
        <div className="absolute -right-20 bottom-8 h-72 w-72 rounded-full bg-amber-400/15 blur-3xl" />
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
        {/* Gallery */}
        <div className="lg:col-span-6">
          <div className="relative overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            <span className="absolute left-3 top-3 z-10 rounded-full bg-[var(--hp-crimson)] px-3 py-1 text-xs font-bold text-white">
              {pack.discount}
            </span>
            <img
              data-testid="hero-main-image"
              src={GALLERY[activeImg].url}
              alt={GALLERY[activeImg].alt}
              className="aspect-square w-full object-cover"
            />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-3">
            {GALLERY.map((g, i) => (
              <button
                key={i}
                data-testid={`hero-thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`overflow-hidden rounded-xl border-2 transition ${
                  activeImg === i ? "border-[var(--hp-crimson)]" : "border-neutral-200"
                }`}
              >
                <img src={g.url} alt={g.alt} className="aspect-square w-full object-cover" />
              </button>
            ))}
          </div>

          {/* Certifications */}
          <div className="mt-5 rounded-2xl border border-neutral-200 bg-white p-4" data-testid="certifications-strip">
            <p className="mb-3 text-center text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Certified & Quality Assured
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              {CERTIFICATIONS.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.label}
                    data-testid={`cert-${c.label}`}
                    className="flex items-center gap-1.5 rounded-full border border-neutral-200 bg-[var(--hp-page)] px-3 py-2"
                  >
                    <Icon className="h-4 w-4 text-[var(--hp-crimson)]" />
                    <span className="text-xs font-bold text-neutral-800">{c.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-6">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-1 text-xs font-bold text-[var(--hp-crimson)]">
              <Eye className="h-3.5 w-3.5" /> 142 people viewing now
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
              <Flame className="h-3.5 w-3.5" /> 159 sold in last 2 hours
            </span>
          </div>

          <h1 className="font-display text-3xl uppercase leading-tight text-neutral-900 sm:text-4xl lg:text-5xl">
            Horse Power Capsule
          </h1>
          <p className="mt-2 text-base text-neutral-600 sm:text-lg">{BRAND.tagline}</p>

          <div className="mt-3 flex items-center gap-2">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="text-sm font-semibold text-neutral-700">
              {BRAND.rating}/5 ({BRAND.reviewCount.toLocaleString("en-IN")}+ reviews)
            </span>
          </div>

          {/* Pack selector */}
          <div className="mt-6 space-y-3" data-testid="pack-selector">
            {PACKS.map((p) => {
              const active = selectedPack === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`pack-option-${p.id}`}
                  onClick={() => setSelectedPack(p.id)}
                  className={`relative flex w-full items-center justify-between rounded-xl border-2 p-4 text-left transition ${
                    active
                      ? "border-[var(--hp-crimson)] bg-rose-50/60"
                      : "border-neutral-200 bg-white hover:border-neutral-300"
                  }`}
                >
                  {p.popular && (
                    <span className="absolute -top-2.5 left-4 rounded-full bg-[var(--hp-gold-light)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                      {p.badge}
                    </span>
                  )}
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                        active ? "border-[var(--hp-crimson)] bg-[var(--hp-crimson)]" : "border-neutral-300"
                      }`}
                    >
                      {active && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <div>
                      <div className="font-bold text-neutral-900">{p.title}</div>
                      <div className="text-xs text-neutral-500">
                        {p.sub} · {p.popular ? "Best Seller" : p.badge}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-num text-2xl font-bold text-[var(--hp-crimson)]">
                      {formatINR(p.price)}
                    </div>
                    <div className="font-num text-sm text-neutral-400 line-through">
                      {formatINR(p.mrp)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <p className="mt-3 text-sm font-semibold text-[var(--hp-crimson)] hp-blink">
            ⚡ Only 14 bottles left — order soon!
          </p>

          {/* CTAs */}
          <div className="mt-5">
            <Button
              data-testid="buy-now-button"
              onClick={handleBuyNow}
              className="hp-pulse h-14 w-full bg-gradient-to-r from-red-600 to-rose-700 text-base font-bold text-white hover:from-red-700 hover:to-rose-800"
            >
              Order Now
            </Button>
          </div>

          {/* Trust badges */}
          <div className="mt-6 grid grid-cols-3 gap-3 border-t border-neutral-200 pt-6">
            {TRUST_BADGES.map((b) => {
              const Icon = ICONS[b.icon];
              return (
                <div key={b.title} className="flex flex-col items-center text-center">
                  <Icon className="h-7 w-7 text-[var(--hp-crimson)]" />
                  <div className="mt-1.5 text-xs font-bold text-neutral-800">{b.title}</div>
                  <div className="text-xs text-neutral-500">{b.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};
